import { Stack, Typography } from "@mui/material";
import { EMPTY_CHARACTER, useBorder } from "@pulse/shared-components";
import {
  AutoCompleteInputField,
  Dialog,
  IconButton,
  LoadingIndicator,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { X } from "lucide-react";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssignProjectToAPIKeyRPC } from "@pulse/pulse-rpcs/lib/external-public-apis/assignProjectToApiKeyRPC";
import { GenerateNewAPIKeyRPC } from "@pulse/pulse-rpcs/lib/external-public-apis/generateNewApiKeyRPC";
import { OneTimeAPICredentials } from "../stores/APIKeyManagementStore";
import { useAPIKeyManagementStore } from "../stores/Hooks";

export type APIKeyProjectSelectionMode = "generate" | "edit";

interface APIKeyProjectSelectionDialogProps {
  open: boolean;
  mode: APIKeyProjectSelectionMode;
  apiKeyId?: string;
  initialProjectIds: string[];
  loadingInitialProjects?: boolean;
  onClose: VoidFunction;
  onGenerated: (credentials: OneTimeAPICredentials) => void;
  onUpdated: VoidFunction;
  onStaleKey: VoidFunction;
  onInvalidProject: VoidFunction;
  onUnexpectedError: (message: string) => void;
}

const DIALOG_WIDTH = "680px";
const SELECTED_PROJECTS_MAX_HEIGHT = "320px";

export const APIKeyProjectSelectionDialog: React.FC<APIKeyProjectSelectionDialogProps> =
  observer(
    ({
      open,
      mode,
      apiKeyId,
      initialProjectIds,
      loadingInitialProjects = false,
      onClose,
      onGenerated,
      onUpdated,
      onStaleKey,
      onInvalidProject,
      onUnexpectedError,
    }) => {
      const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(
        [],
      );
      const [searchText, setSearchText] = useState(EMPTY_CHARACTER);
      const isSearchFieldCleared = useRef(false);
      const { t } = useTranslation();
      const spacing = useSpacing();
      const typography = useTypography();
      const tokens = useFoundationColorTokens();
      const border = useBorder();
      const store = useAPIKeyManagementStore();
      const isGenerateMode = mode === "generate";

      useEffect(() => {
        if (open) {
          setSelectedProjectIds(initialProjectIds);
          setSearchText(EMPTY_CHARACTER);
        }
      }, [initialProjectIds, open]);

      const initialProjectIdSet = useMemo(
        () => new Set(initialProjectIds),
        [initialProjectIds],
      );
      const hasChanged =
        selectedProjectIds.length !== initialProjectIdSet.size ||
        selectedProjectIds.some(
          (projectId) => !initialProjectIdSet.has(projectId),
        );

      const removeProject = (projectId: string): void => {
        setSelectedProjectIds((projectIds) =>
          projectIds.filter((selectedId) => selectedId !== projectId),
        );
      };

      const handleSave = async (): Promise<void> => {
        try {
          if (isGenerateMode) {
            const credentials = await store.generateAPIKey(selectedProjectIds);
            if (credentials) {
              onGenerated(credentials);
              return;
            }
            if (
              store.rpcError === GenerateNewAPIKeyRPC.RPCError.InvalidProjectId
            ) {
              await store.getActiveProjects();
              onInvalidProject();
            }
            return;
          }

          if (!apiKeyId) return;
          const updated = await store.updateProjectAssignments(
            apiKeyId,
            selectedProjectIds,
          );
          if (updated) {
            onUpdated();
            return;
          }
          switch (store.rpcError) {
            case AssignProjectToAPIKeyRPC.RPCError.InvalidApiKey:
              onStaleKey();
              break;
            case AssignProjectToAPIKeyRPC.RPCError.InvalidProjectId:
              await store.getActiveProjects();
              onInvalidProject();
              break;
          }
        } catch {
          onUnexpectedError(
            t(
              isGenerateMode
                ? "externalPublicApis.apiKeyManagementGenerateUnexpectedError"
                : "externalPublicApis.apiKeyManagementUpdateUnexpectedError",
            ),
          );
        }
      };

      return (
        <Dialog
          open={open}
          title={t(
            isGenerateMode
              ? "externalPublicApis.apiKeyManagementGenerateDialogTitle"
              : "externalPublicApis.apiKeyManagementEditDialogTitle",
          )}
          primaryButtonText={t(
            isGenerateMode
              ? "externalPublicApis.apiKeyManagementGenerate"
              : "externalPublicApis.apiKeyManagementSaveChanges",
          )}
          onPrimaryButtonClick={handleSave}
          isPrimaryButtonDisabled={
            loadingInitialProjects ||
            selectedProjectIds.length === 0 ||
            (!isGenerateMode && !hasChanged)
          }
          secondaryButtonText={t("common.cancel")}
          onSecondaryButtonClick={onClose}
          isSecondaryButtonDisabled={
            loadingInitialProjects || store.loadingAction
          }
          disableBackdropClick
          width={DIALOG_WIDTH}
        >
          {loadingInitialProjects ? (
            <Stack width="100%" height="120px">
              <LoadingIndicator
                variant="container"
                loadingText={t(
                  "externalPublicApis.apiKeyManagementLoadingProjects",
                )}
                isLoading
              />
            </Stack>
          ) : (
            <Stack width="100%" gap={spacing.spaceMD}>
              <Stack gap={spacing.spaceSM}>
                <Typography {...typography.b2} color={tokens.labelSubtle}>
                  {t(
                    isGenerateMode
                      ? "externalPublicApis.apiKeyManagementGenerateDialogDescription"
                      : "externalPublicApis.apiKeyManagementEditDialogDescription",
                  )}
                </Typography>
                <AutoCompleteInputField
                  inputValue={searchText}
                  disabled={store.loadingAction || store.loadingProjects}
                  placeholder={t(
                    "externalPublicApis.apiKeyManagementProjectSearchPlaceholder",
                  )}
                  label={EMPTY_CHARACTER}
                  noOptionsText={t(
                    "externalPublicApis.apiKeyManagementNoProjectsFound",
                  )}
                  options={store.activeProjects
                    .filter(
                      ({ projectId }) =>
                        !selectedProjectIds.includes(projectId),
                    )
                    .map(({ projectId, projectName }) => ({
                      id: projectId,
                      label: projectName,
                    }))}
                  onInputChange={(inputText) => {
                    if (isSearchFieldCleared.current) {
                      isSearchFieldCleared.current = false;
                      setSearchText(EMPTY_CHARACTER);
                      return;
                    }
                    setSearchText(inputText ?? EMPTY_CHARACTER);
                  }}
                  onChange={(selectedItem) => {
                    isSearchFieldCleared.current = true;
                    setSearchText(EMPTY_CHARACTER);
                    if (selectedItem?.id === null || selectedItem === null) {
                      return;
                    }
                    setSelectedProjectIds((projectIds) => [
                      ...projectIds,
                      String(selectedItem.id),
                    ]);
                  }}
                />
              </Stack>
              <Stack gap={spacing.spaceMD}>
                <Stack
                  padding={`${spacing.spaceXS} 0`}
                  borderBottom={border.default}
                >
                  <Typography {...typography.sh3} color={tokens.label}>
                    {t("externalPublicApis.apiKeyManagementSelectedProjects")}
                  </Typography>
                </Stack>
                <Stack
                  gap={spacing.spaceSM}
                  maxHeight={SELECTED_PROJECTS_MAX_HEIGHT}
                  paddingRight={spacing.spaceXS}
                  sx={{ overflowY: "auto" }}
                >
                  {selectedProjectIds.length === 0 ? (
                    <Typography
                      {...typography.b2}
                      color={tokens.labelLowEmphasis}
                    >
                      {t(
                        "externalPublicApis.apiKeyManagementNoProjectsSelected",
                      )}
                    </Typography>
                  ) : (
                    selectedProjectIds.map((projectId) => {
                      const project = store.activeProjects.find(
                        (activeProject) =>
                          activeProject.projectId === projectId,
                      );
                      return (
                        <Stack
                          key={projectId}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={spacing.spaceSM}
                        >
                          <Typography
                            {...typography.b1}
                            color={tokens.label}
                            sx={{ overflowWrap: "anywhere" }}
                          >
                            {project?.projectName ??
                              t(
                                "externalPublicApis.apiKeyManagementUnavailableProject",
                                { projectId },
                              )}
                          </Typography>
                          <IconButton
                            name="removeAPIKeyProject"
                            variant="plain-neutral"
                            size="medium"
                            icon={<X />}
                            onClick={() => removeProject(projectId)}
                            disabled={store.loadingAction}
                          />
                        </Stack>
                      );
                    })
                  )}
                </Stack>
              </Stack>
            </Stack>
          )}
        </Dialog>
      );
    },
  );
